import { motion } from "framer-motion";
import { Twitter, Linkedin, Instagram, Facebook, Mail, Phone, MapPin } from "lucide-react";
import { Link } from "react-router-dom";

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const links = {
    services: [
      { label: "CHATGPT ADS", href: "#services" },
      { label: "GEMINI ADS", href: "#services" },
      { label: "PERPLEXITY ADS", href: "#services" },
      { label: "WEBSITE DESIGN", href: "#services" },
    ],
    company: [
      { label: "HOW IT WORKS", href: "#how-it-works" },
      { label: "INDUSTRIES", href: "#industries" },
      { label: "FAQ", href: "#faq" },
      { label: "CONTACT", href: "#contact" },
    ],
    legal: [
      { label: "PRIVACY POLICY", href: "/privacy-policy", isRoute: true },
      { label: "TERMS OF SERVICE", href: "/terms-of-service", isRoute: true },
      { label: "REFUND POLICY", href: "/refund-policy", isRoute: true },
      { label: "DISCLAIMER", href: "/disclaimer", isRoute: true },
    ],
  };

  const socials = [
    { icon: Twitter, href: "#", label: "Twitter" },
    { icon: Linkedin, href: "#", label: "LinkedIn" },
    { icon: Instagram, href: "#", label: "Instagram" },
    { icon: Facebook, href: "#", label: "Facebook" },
  ];

  return (
    <footer className="border-t border-border bg-card/50">
      <div className="container mx-auto px-4 py-10 md:py-14 max-w-[1280px]">
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-8 mb-10">
          {/* Brand */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-2"
          >
            <a href="#" className="inline-block mb-4">
              <span className="text-xl font-bold">
                <span className="text-gradient">AI Ads</span>
                <span className="text-foreground"> Flow</span>
              </span>
            </a>
            <p className="text-sm text-muted-foreground mb-4">
              The future of local business advertising. Get found where AI searches first.
            </p>
            
            {/* Contact Info */}
            <div className="space-y-2 mb-4">
              <a href="mailto:support@aiadsflow.com" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors">
                <Mail className="h-4 w-4 text-accent" />
                support@aiadsflow.com
              </a>
              <a href="tel:+14075452129" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors">
                <Phone className="h-4 w-4 text-accent" />
                (407) 545-2129
              </a>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <MapPin className="h-4 w-4 text-accent" />
                Serving USA & Canada
              </div>
            </div>

            <div className="flex gap-3">
              {socials.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  aria-label={social.label}
                  className="w-10 h-10 rounded-lg bg-secondary/50 flex items-center justify-center hover:bg-primary/20 hover:text-primary hover:scale-110 transition-all"
                >
                  <social.icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </motion.div>

          {/* Services */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <h4 className="font-semibold mb-4 text-sm tracking-wider">SERVICES</h4>
            <ul className="space-y-2">
              {links.services.map((link) => (
                <li key={link.label}>
                  <a
                    href={link.href}
                    className="text-xs text-muted-foreground hover:text-primary transition-colors tracking-wide"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Company */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <h4 className="font-semibold mb-4 text-sm tracking-wider">COMPANY</h4>
            <ul className="space-y-2">
              {links.company.map((link) => (
                <li key={link.label}>
                  <a
                    href={link.href}
                    className="text-xs text-muted-foreground hover:text-primary transition-colors tracking-wide"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Legal */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <h4 className="font-semibold mb-4 text-sm tracking-wider">LEGAL</h4>
            <ul className="space-y-2">
              {links.legal.map((link) => (
                <li key={link.label}>
                  {link.isRoute ? (
                    <Link
                      to={link.href}
                      className="text-xs text-muted-foreground hover:text-primary transition-colors tracking-wide"
                    >
                      {link.label}
                    </Link>
                  ) : (
                    <a
                      href={link.href}
                      className="text-xs text-muted-foreground hover:text-primary transition-colors tracking-wide"
                    >
                      {link.label}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </motion.div>
        </div>

        {/* Bottom */}
        <div className="pt-8 border-t border-border flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-muted-foreground">
          <p>© {currentYear} AI Ads Flow. All rights reserved.</p>
          <p>Built for the future of search.</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
